import { useEffect, useRef, useState } from 'react';

import DOMPurify from 'isomorphic-dompurify';

import { Button } from '@ui/uikit/components/common/Button';
import { Typography } from '@ui/uikit/components/common/Typography';
import { cn } from '@ui/uikit/utils';

import styles from './TruncTextWithReadMore.module.scss';

interface TruncTextWithReadMoreProps {
  text: string;
  maxLines?: number;
}

const TruncTextWithReadMore = ({ text, maxLines = 3 }: TruncTextWithReadMoreProps) => {
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = contentRef.current;

    if (!element) {
      return;
    }

    const checkTruncationNeeded = () => {
      const styles = window.getComputedStyle(element);
      const lineHeight = parseInt(styles.lineHeight) || 20;
      const maxHeight = maxLines * lineHeight;

      setNeedsTruncation(element.scrollHeight > maxHeight);
    };

    checkTruncationNeeded();

    const observer = new ResizeObserver(checkTruncationNeeded);
    observer.observe(element);

    return () => observer.disconnect();
  }, [text, maxLines]);

  return (
    <div className={cn(styles['container'])}>
      <Typography variant="caption-s">
        <div
          ref={contentRef}
          className={cn(styles['content'], {
            [styles['textTruncated']]: needsTruncation && !isExpanded,
          })}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
        />
      </Typography>

      {needsTruncation && (
        <Button variant="secondary" onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? 'Show Less' : 'Read More'}
        </Button>
      )}
    </div>
  );
};

export default TruncTextWithReadMore;
